import React, { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import MarketSelectorHeader from "components/ui/MarketSelectorHeader";
import DataRefreshIndicator from "components/ui/DataRefreshIndicator";
import ExportControlPanel from "components/ui/ExportControlPanel";
import FilterPersistenceManager from "components/ui/FilterPersistenceManager";
import Icon from "components/AppIcon";

import KPICard from "./components/KPICard";
import PriceHistoryChart from "./components/PriceHistoryChart";
import NeighborhoodPerformanceTable from "./components/NeighborhoodPerformanceTable";
import SeasonalHeatmap from "./components/SeasonalHeatmap";
import MarketSegmentChart from "./components/MarketSegmentChart";
import AbsorptionRateChart from "./components/AbsorptionRateChart";
import BookmarkPanel from "./components/BookmarkPanel";

const KPI_DATA = [
  {
    title: "Median Sale Price",
    value: "285K",
    prefix: "$",
    suffix: "",
    change: 5.8,
    changeLabel: "vs. same period last year",
    icon: "DollarSign",
    trend: "up",
  },
  {
    title: "Active Inventory",
    value: "1,243",
    prefix: "",
    suffix: " units",
    change: -7.2,
    changeLabel: "vs. same period last year",
    icon: "Home",
    trend: "down",
  },
  {
    title: "Avg Days on Market",
    value: "28",
    prefix: "",
    suffix: " days",
    change: -10.8,
    changeLabel: "faster than last year",
    icon: "Clock",
    trend: "up",
  },
  {
    title: "Market Appreciation",
    value: "5.8",
    prefix: "",
    suffix: "%",
    change: 1.2,
    changeLabel: "YoY rate change",
    icon: "TrendingUp",
    trend: "up",
  },
];

const TIME_HORIZONS = [
  { label: "3M", value: "3m" },
  { label: "6M", value: "6m" },
  { label: "1Y", value: "12m" },
  { label: "2Y", value: "24m" },
  { label: "5Y", value: "60m" },
];

export default function MarketTrendsAnalytics() {
  const location = useLocation();
  const [timeHorizon, setTimeHorizon] = useState("12m");
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(async () => {
    await new Promise(r => setTimeout(r, 800));
    setRefreshKey(k => k + 1);
  }, []);

  const handleExport = useCallback(async (option) => {
    await new Promise(r => setTimeout(r, 1200));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header + Nav */}
      <MarketSelectorHeader />
      {/* Page offset for fixed header (60px) + tab nav (49px) */}
      <div className="pt-[109px] lg:pt-[109px] pb-20 lg:pb-8">
        {/* Toolbar */}
        <div className="sticky top-[109px] z-[900] bg-background border-b border-border">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-between gap-3">
            {/* Left: Title + Time Horizon */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="hidden sm:block">
                <h1 className="font-heading font-bold text-base text-foreground leading-tight">
                  Market Intelligence
                </h1>
                <p className="text-xs font-caption text-muted-foreground">Omaha Metro · MLS Certified</p>
              </div>
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                {TIME_HORIZONS?.map(t => (
                  <button
                    key={t?.value}
                    onClick={() => setTimeHorizon(t?.value)}
                    className={`px-2.5 py-1 rounded-md text-xs font-caption font-semibold transition-standard ${
                      timeHorizon === t?.value
                        ? "bg-card text-primary shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {t?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Controls */}
            <div className="flex items-center gap-2 flex-wrap">
              <DataRefreshIndicator onRefresh={handleRefresh} />
              <FilterPersistenceManager currentPath="/market-trends-analytics" />
              <BookmarkPanel />
              <ExportControlPanel currentPath="/market-trends-analytics" onExport={handleExport} />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6 space-y-4 md:space-y-6">

          {/* KPI Row */}
          <section aria-label="Key performance indicators">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              {KPI_DATA?.map((kpi) => (
                <KPICard key={kpi?.title} {...kpi} />
              ))}
            </div>
          </section>

          {/* Main Visualization + Neighborhood Table */}
          <section aria-label="Price history and neighborhood performance">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
              {/* Price History Chart - 8 cols */}
              <div className="lg:col-span-8">
                <PriceHistoryChart key={refreshKey} />
              </div>
              {/* Neighborhood Table - 4 cols */}
              <div className="lg:col-span-4">
                <NeighborhoodPerformanceTable />
              </div>
            </div>
          </section>

          {/* Secondary Charts Row */}
          <section aria-label="Market segment and absorption analysis">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <MarketSegmentChart />
              <AbsorptionRateChart />
            </div>
          </section>

          {/* Full-width Seasonal Heatmap */}
          <section aria-label="Seasonal market patterns heatmap">
            <SeasonalHeatmap />
          </section>

          {/* Market Insights Footer Strip */}
          <section aria-label="Market insights summary">
            <div className="card-base">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <h3 className="font-heading font-semibold text-base text-foreground">Market Intelligence Summary</h3>
                <span className="text-xs font-caption text-muted-foreground bg-muted px-2 py-1 rounded">
                  Updated Mar 3, 2025 · 10:30 AM EST
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    icon: "Flame",
                    color: "text-red-500",
                    bg: "bg-red-50",
                    title: "Market Condition",
                    value: "Strong Seller's Market",
                    detail: "2.8 months of inventory — well below 6-month balanced threshold",
                  },
                  {
                    icon: "BarChart3",
                    color: "text-blue-600",
                    bg: "bg-blue-50",
                    title: "Price Momentum",
                    value: "+5.8% YoY Appreciation",
                    detail: "Accelerating from +4.5% last year; driven by low inventory and job growth",
                  },
                  {
                    icon: "Calendar",
                    color: "text-emerald-600",
                    bg: "bg-emerald-50",
                    title: "Seasonal Outlook",
                    value: "Spring Rally Underway",
                    detail: "Volume up 16.4% MoM; peak activity expected May–June",
                  },
                  {
                    icon: "Target",
                    color: "text-purple-600",
                    bg: "bg-purple-50",
                    title: "Best Opportunity",
                    value: "$250K–$350K Segment",
                    detail: "Highest appreciation (+5.8%) with manageable 22-day DOM",
                  },
                ]?.map(item => (
                  <div key={item?.title} className="flex items-start gap-3">
                    <div className={`flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 ${item?.bg}`}>
                      <Icon name={item?.icon} size={18} color="currentColor" className={item?.color} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-caption font-medium text-muted-foreground">{item?.title}</p>
                      <p className="text-sm font-heading font-semibold text-foreground leading-tight">{item?.value}</p>
                      <p className="text-xs font-caption text-muted-foreground mt-0.5 line-clamp-2">{item?.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}